import React, { useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext, Campaign, Donation } from '../contexts/AppContext';
import { formatCurrency, generateReference } from '../utils/formatters';
import { Modal } from '../components/ui/Modal';
import { BillvastLogo } from '../components/ui/BillvastLogo';
import { toast } from 'sonner';
import { Heart, ShieldCheck, CheckCircle2, ArrowLeft, CreditCard, Building, Copy, Users, Clock, Loader2, Smartphone, Hash } from 'lucide-react';
export function GuestDonate() {
  const {
    state,
    dispatch
  } = useAppContext();
  const {
    campaigns
  } = state;
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Modal State
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  // Step 1: Info
  const [amount, setAmount] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  // Step 2: Payment Method
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'bank_transfer' | 'google_pay' | 'apple_pay' | 'mobile_pay' | 'ussd'>('card');
  // Card Details
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardName, setCardName] = useState('');
  // Success Details
  const [transactionRef, setTransactionRef] = useState('');
  const verifiedCampaigns = useMemo(() => {
    return campaigns.filter(c => c.verificationStatus === 'verified' && c.status === 'Active');
  }, [campaigns]);
  const openDonateModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setStep(1);
    setAmount('');
    setGuestName('');
    setGuestEmail('');
    setMessage('');
    setIsAnonymous(false);
    setPaymentMethod('card');
    setCardNumber('');
    setExpiry('');
    setCvv('');
    setCardName('');
    setIsModalOpen(true);
  };
  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (Number(amount) < 500) {
      toast.error('Minimum donation is ₦500');
      return;
    }
    if (!guestName.trim() || !guestEmail.trim()) {
      toast.error('Please provide your name and email');
      return;
    }
    setStep(2);
  };
  const handlePaymentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (paymentMethod === 'card') {
      if (cardNumber.replace(/\s/g, '').length !== 16) {
        toast.error('Please enter a valid 16-digit card number');
        return;
      }
      if (!/^\d{2}\/\d{2}$/.test(expiry)) {
        toast.error('Please enter a valid expiry date (MM/YY)');
        return;
      }
      if (cvv.length !== 3) {
        toast.error('Please enter a valid 3-digit CVV');
        return;
      }
      if (!cardName.trim()) {
        toast.error('Please enter the cardholder name');
        return;
      }
    }
    setStep(3); // Processing
    setTimeout(() => {
      processDonation();
    }, 2000);
  };
  const processDonation = () => {
    if (!selectedCampaign) return;
    const ref = generateReference('GDN');
    setTransactionRef(ref);
    const donation: Donation = {
      id: `don_${Date.now()}`,
      campaignId: selectedCampaign.id,
      donorId: 'guest',
      donorName: isAnonymous ? 'Anonymous' : guestName,
      amount: Number(amount),
      tipAmount: 0,
      message,
      isAnonymous,
      isRecurring: false,
      paymentMethod,
      transactionReference: ref,
      status: 'completed',
      createdAt: new Date().toISOString()
    };
    dispatch({
      type: 'ADD_DONATION',
      payload: donation
    });
    dispatch({
      type: 'UPDATE_CAMPAIGN_RAISED',
      payload: {
        id: selectedCampaign.id,
        amount: Number(amount)
      }
    });
    setStep(4); // Success
    toast.success('Donation successful!');
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };
  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };
  return <div className="min-h-screen bg-gray-50 pb-20" data-id="element-1000">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30" data-id="element-1001">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-1002">
          <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors" data-id="element-1003">
            <ArrowLeft size={20} data-id="element-1004" />
            <span className="font-medium hidden sm:inline" data-id="element-1005">Back to Home</span>
          </Link>

          <Link to="/" data-id="element-1006">
            <BillvastLogo size="md" showText={true} data-id="element-1007" />
          </Link>

          <Link to="/login" className="text-sm font-semibold text-brand-primary hover:text-brand-primary-dark transition-colors" data-id="element-1008">
            Sign In
          </Link>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-brand-primary-dark text-white py-12 px-4 relative overflow-hidden" data-id="element-1009">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary rounded-full mix-blend-multiply filter blur-3xl opacity-50 translate-x-1/2 -translate-y-1/2" data-id="element-1010"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-1011">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-sm font-medium mb-4" data-id="element-1012">
            <Heart size={16} className="text-brand-accent" data-id="element-1013" />
            Donate as Guest
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1014">
            Support Verified Causes
          </h1>
          <p className="text-white/80 text-lg" data-id="element-1015">
            No account needed. Make a secure donation via card or bank transfer
            to campaigns verified by our Trust & Safety team.
          </p>
        </div>
      </div>

      {/* Campaign Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1016">
        {verifiedCampaigns.length === 0 ? <div className="text-center py-20 bg-white rounded-3xl border border-gray-100" data-id="element-1017">
            <ShieldCheck size={48} className="mx-auto mb-4 text-gray-300" data-id="element-1018" />
            <h3 className="text-xl font-semibold text-brand-text-dark mb-2" data-id="element-1019">
              No verified campaigns right now
            </h3>
            <p className="text-brand-text-light" data-id="element-1020">
              Check back later for causes to support.
            </p>
          </div> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1021">
            {verifiedCampaigns.map(campaign => {
          const progress = Math.min(Math.round(campaign.raisedAmount / campaign.targetAmount * 100), 100);
          const daysLeft = Math.max(0, Math.ceil((new Date(campaign.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
          return <motion.div key={campaign.id} whileHover={{
            y: -4
          }} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full" data-id="element-1022">
                  <div className="relative h-48 overflow-hidden" data-id="element-1023">
                    <img src={campaign.imageUrl} alt={campaign.title} className="w-full h-full object-cover" data-id="element-1024" />
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-brand-primary-dark text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm" data-id="element-1025">
                      {campaign.category}
                    </div>
                  </div>

                  <div className="p-6 flex flex-col flex-1" data-id="element-1026">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-brand-primary-medium mb-2 bg-brand-primary/10 w-fit px-2 py-0.5 rounded-md" data-id="element-1027">
                      <ShieldCheck size={14} data-id="element-1028" /> VERIFIED CAUSE
                    </div>

                    <h3 className="font-bold text-brand-text-dark text-lg leading-tight mb-3 line-clamp-2" data-id="element-1029">
                      {campaign.title}
                    </h3>

                    <div className="flex items-center gap-2 mb-5" data-id="element-1030">
                      <img src={campaign.organizerAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(campaign.organizerName)}&background=f3f4f6&color=6b7280`} alt={campaign.organizerName} className="w-6 h-6 rounded-full" data-id="element-1031" />
                      <span className="text-xs text-brand-text-light font-medium truncate" data-id="element-1032">
                        by {campaign.organizerName}
                      </span>
                    </div>

                    <div className="mt-auto space-y-4" data-id="element-1033">
                      <div data-id="element-1034">
                        <div className="flex justify-between text-sm mb-1.5" data-id="element-1035">
                          <span className="font-bold text-brand-text-dark" data-id="element-1036">
                            {formatCurrency(campaign.raisedAmount)}
                          </span>
                          <span className="text-brand-text-light text-xs" data-id="element-1037">
                            of {formatCurrency(campaign.targetAmount)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden" data-id="element-1038">
                          <div className="bg-brand-primary h-full rounded-full" style={{
                      width: `${progress}%`
                    }} data-id="element-1039" />
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-brand-text-light font-medium" data-id="element-1040">
                        <span className="flex items-center gap-1" data-id="element-1041">
                          <Users size={14} data-id="element-1042" /> {campaign.donorCount || 0} donors
                        </span>
                        <span className="flex items-center gap-1" data-id="element-1043">
                          <Clock size={14} data-id="element-1044" />{' '}
                          {daysLeft > 0 ? `${daysLeft} days left` : 'Ended'}
                        </span>
                      </div>

                      <button onClick={() => openDonateModal(campaign)} className="w-full py-3 bg-brand-primary hover:bg-brand-primary-medium text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-1045">
                        <Heart size={18} data-id="element-1046" /> Donate Now
                      </button>
                    </div>
                  </div>
                </motion.div>;
        })}
          </div>}
      </main>

      {/* Guest Donation Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={step === 4 ? 'Donation Successful' : 'Donate as Guest'} maxWidth="max-w-md" data-id="element-1047">
        <AnimatePresence mode="wait" data-id="element-1048">
          {step === 1 && <motion.form key="step1" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} onSubmit={handleStep1Submit} className="space-y-5" data-id="element-1049">
              {selectedCampaign && <div className="bg-gray-50 p-3 rounded-xl flex items-center gap-3 mb-2" data-id="element-1050">
                  <img src={selectedCampaign.imageUrl} alt="" className="w-12 h-12 rounded-lg object-cover" data-id="element-1051" />
                  <div data-id="element-1052">
                    <p className="text-xs text-brand-text-light" data-id="element-1053">Supporting</p>
                    <p className="text-sm font-bold text-brand-text-dark line-clamp-1" data-id="element-1054">
                      {selectedCampaign.title}
                    </p>
                  </div>
                </div>}

              <div data-id="element-1055">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1056">
                  Donation Amount
                </label>
                <div className="relative" data-id="element-1057">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-lg" data-id="element-1058">
                    ₦
                  </span>
                  <input type="number" required min="500" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" className="w-full pl-10 pr-4 py-3 text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-brand-primary focus:ring-0 outline-none transition-colors" data-id="element-1059" />
                </div>
                <div className="grid grid-cols-4 gap-2 mt-2" data-id="element-1060">
                  {[5000, 10000, 25000, 50000].map(amt => <button key={amt} type="button" onClick={() => setAmount(amt.toString())} className="py-1.5 border border-gray-200 rounded-lg text-sm font-medium hover:border-brand-primary hover:text-brand-primary transition-colors" data-id="element-1061">
                      {amt / 1000}k
                    </button>)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-1062">
                <div data-id="element-1063">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1064">
                    Full Name
                  </label>
                  <input type="text" required value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="John Doe" className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-brand-primary outline-none" data-id="element-1065" />
                </div>
                <div data-id="element-1066">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1067">
                    Email
                  </label>
                  <input type="email" required value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="john@example.com" className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-brand-primary outline-none" data-id="element-1068" />
                </div>
              </div>

              <div data-id="element-1069">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1070">
                  Message (Optional)
                </label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Words of encouragement..." rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-brand-primary outline-none resize-none" data-id="element-1071" />
              </div>

              <label className="flex items-center gap-2 cursor-pointer" data-id="element-1072">
                <input type="checkbox" checked={isAnonymous} onChange={e => setIsAnonymous(e.target.checked)} className="w-4 h-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded" data-id="element-1073" />
                <span className="text-sm text-gray-700" data-id="element-1074">
                  Don't display my name publicly
                </span>
              </label>

              <button type="submit" className="w-full py-3.5 bg-brand-primary hover:bg-brand-primary-medium text-white font-bold rounded-xl transition-colors shadow-md" data-id="element-1075">
                Continue to Payment
              </button>
            </motion.form>}

          {step === 2 && <motion.form key="step2" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} onSubmit={handlePaymentSubmit} className="space-y-6" data-id="element-1076">
              <div className="flex items-center gap-2 mb-4" data-id="element-1077">
                <button type="button" onClick={() => setStep(1)} className="text-gray-400 hover:text-gray-700" data-id="element-1078">
                  <ArrowLeft size={20} data-id="element-1079" />
                </button>
                <span className="font-semibold text-brand-text-dark" data-id="element-1080">
                  Payment Method
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2" data-id="element-1081">
                <button type="button" onClick={() => setPaymentMethod('card')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${paymentMethod === 'card' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1082">
                  <CreditCard size={20} data-id="element-1083" />
                  <span className="text-xs font-semibold" data-id="element-1084">Card</span>
                </button>
                <button type="button" onClick={() => setPaymentMethod('bank_transfer')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${paymentMethod === 'bank_transfer' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1085">
                  <Building size={20} data-id="element-1086" />
                  <span className="text-xs font-semibold" data-id="element-1087">Transfer</span>
                </button>
                <button type="button" onClick={() => setPaymentMethod('ussd')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${paymentMethod === 'ussd' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1088">
                  <Hash size={20} data-id="element-1089" />
                  <span className="text-xs font-semibold" data-id="element-1090">USSD</span>
                </button>
                <button type="button" onClick={() => setPaymentMethod('google_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${paymentMethod === 'google_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1091">
                  <div className="flex items-center gap-1 font-bold text-gray-800 text-sm" data-id="element-1092">
                    <span className="text-blue-500" data-id="element-1093">G</span>Pay
                  </div>
                  <span className="text-xs font-semibold" data-id="element-1094">Google Pay</span>
                </button>
                <button type="button" onClick={() => setPaymentMethod('apple_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${paymentMethod === 'apple_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1095">
                  <div className="font-bold text-gray-800 text-sm flex items-center" data-id="element-1096">
                     Pay
                  </div>
                  <span className="text-xs font-semibold" data-id="element-1097">Apple Pay</span>
                </button>
                <button type="button" onClick={() => setPaymentMethod('mobile_pay')} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1.5 transition-colors ${paymentMethod === 'mobile_pay' ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark' : 'border-gray-100 hover:border-gray-200 text-gray-500'}`} data-id="element-1098">
                  <Smartphone size={20} data-id="element-1099" />
                  <span className="text-xs font-semibold" data-id="element-1100">Mobile Pay</span>
                </button>
              </div>

              {paymentMethod === 'card' ? <div className="space-y-4 animate-fade-in" data-id="element-1101">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1102">
                    {/* Visa */}
                    <div className="h-6 px-2 bg-blue-50 rounded flex items-center justify-center text-blue-800 font-bold italic text-xs border border-blue-100" data-id="element-1103">
                      VISA
                    </div>
                    {/* Mastercard */}
                    <div className="h-6 px-2 bg-gray-50 rounded flex items-center justify-center border border-gray-200" data-id="element-1104">
                      <div className="flex -space-x-1.5" data-id="element-1105">
                        <div className="w-3 h-3 rounded-full bg-red-500 mix-blend-multiply" data-id="element-1106"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-500 mix-blend-multiply" data-id="element-1107"></div>
                      </div>
                    </div>
                    {/* Amex */}
                    <div className="h-6 px-2 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-[10px] tracking-tighter" data-id="element-1108">
                      AMEX
                    </div>
                    {/* Verve */}
                    <div className="h-6 px-2 bg-red-600 rounded flex items-center justify-center text-white font-bold italic text-[10px]" data-id="element-1109">
                      Verve
                    </div>
                  </div>
                  <div data-id="element-1110">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1111">
                      Card Number
                    </label>
                    <input type="text" required maxLength={19} value={cardNumber} onChange={e => setCardNumber(formatCardNumber(e.target.value))} placeholder="0000 0000 0000 0000" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-brand-primary outline-none font-mono" data-id="element-1112" />
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1113">
                    <div data-id="element-1114">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1115">
                        Expiry
                      </label>
                      <input type="text" required maxLength={5} value={expiry} onChange={e => {
                  let val = e.target.value.replace(/\D/g, '');
                  if (val.length >= 2) val = val.substring(0, 2) + '/' + val.substring(2, 4);
                  setExpiry(val);
                }} placeholder="MM/YY" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-brand-primary outline-none font-mono" data-id="element-1116" />
                    </div>
                    <div data-id="element-1117">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1118">
                        CVV
                      </label>
                      <input type="password" required maxLength={3} value={cvv} onChange={e => setCvv(e.target.value.replace(/\D/g, ''))} placeholder="123" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-brand-primary outline-none font-mono" data-id="element-1119" />
                    </div>
                  </div>
                  <div data-id="element-1120">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1121">
                      Cardholder Name
                    </label>
                    <input type="text" required value={cardName} onChange={e => setCardName(e.target.value)} placeholder="JOHN DOE" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-brand-primary outline-none uppercase" data-id="element-1122" />
                  </div>
                </div> : paymentMethod === 'bank_transfer' ? <div className="space-y-4 animate-fade-in" data-id="element-1123">
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-100" data-id="element-1124">
                    <p className="text-sm text-blue-800 mb-3" data-id="element-1125">
                      Please transfer exactly{' '}
                      <strong data-id="element-1126">{formatCurrency(Number(amount))}</strong> to the
                      account below. Use your email as the reference.
                    </p>
                    <div className="bg-white p-4 rounded-lg space-y-3 shadow-sm" data-id="element-1127">
                      <div className="flex justify-between items-center" data-id="element-1128">
                        <span className="text-xs text-gray-500" data-id="element-1129">Bank</span>
                        <span className="font-semibold text-gray-900" data-id="element-1130">
                          GTBank
                        </span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1131">
                        <span className="text-xs text-gray-500" data-id="element-1132">
                          Account Number
                        </span>
                        <div className="flex items-center gap-2" data-id="element-1133">
                          <span className="font-bold text-gray-900 text-lg tracking-wider" data-id="element-1134">
                            0112345678
                          </span>
                          <button type="button" onClick={() => copyToClipboard('0112345678')} className="text-brand-primary hover:text-brand-primary-dark p-1" data-id="element-1135">
                            <Copy size={16} data-id="element-1136" />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1137">
                        <span className="text-xs text-gray-500" data-id="element-1138">
                          Account Name
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-1139">
                          Billvast Crowdfund
                        </span>
                      </div>
                    </div>
                  </div>
                </div> : paymentMethod === 'ussd' ? <div className="space-y-4 animate-fade-in" data-id="element-1140">
                  <div data-id="element-1141">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1142">
                      Select Bank for USSD
                    </label>
                    <select className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-brand-primary outline-none bg-white" data-id="element-1143">
                      <option value="*737#" data-id="element-1144">GTBank (*737#)</option>
                      <option value="*901#" data-id="element-1145">Access Bank (*901#)</option>
                      <option value="*770#" data-id="element-1146">Fidelity Bank (*770#)</option>
                      <option value="*894#" data-id="element-1147">First Bank (*894#)</option>
                      <option value="*822#" data-id="element-1148">Sterling Bank (*822#)</option>
                      <option value="*919#" data-id="element-1149">UBA (*919#)</option>
                      <option value="*966#" data-id="element-1150">Zenith Bank (*966#)</option>
                    </select>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 text-center" data-id="element-1151">
                    <p className="text-sm text-gray-600 mb-2" data-id="element-1152">
                      Dial the code below on your mobile phone to complete the
                      payment.
                    </p>
                    <div className="text-2xl font-bold text-brand-text-dark tracking-wider" data-id="element-1153">
                      *737*000*1234#
                    </div>
                  </div>
                </div> : <div className="space-y-4 animate-fade-in" data-id="element-1154">
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center" data-id="element-1155">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm" data-id="element-1156">
                      {paymentMethod === 'google_pay' && <span className="font-bold text-xl" data-id="element-1157">
                          <span className="text-blue-500" data-id="element-1158">G</span>Pay
                        </span>}
                      {paymentMethod === 'apple_pay' && <span className="font-bold text-2xl" data-id="element-1159"></span>}
                      {paymentMethod === 'mobile_pay' && <Smartphone size={32} className="text-gray-700" data-id="element-1160" />}
                    </div>
                    <h4 className="font-semibold text-brand-text-dark mb-2" data-id="element-1161">
                      Pay with{' '}
                      {paymentMethod.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </h4>
                    <p className="text-sm text-gray-500" data-id="element-1162">
                      You will be redirected to complete your{' '}
                      {formatCurrency(Number(amount))} donation securely.
                    </p>
                  </div>
                </div>}

              <button type="submit" className="w-full py-3.5 bg-brand-primary hover:bg-brand-primary-medium text-white font-bold rounded-xl transition-colors shadow-md" data-id="element-1163">
                Pay {formatCurrency(Number(amount))}
              </button>
            </motion.form>}

          {step === 3 && <motion.div key="step3" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="py-12 flex flex-col items-center justify-center text-center space-y-4" data-id="element-1164">
              <Loader2 size={48} className="text-brand-primary animate-spin" data-id="element-1165" />
              <h3 className="text-lg font-semibold text-brand-text-dark" data-id="element-1166">
                Processing Payment...
              </h3>
              <p className="text-brand-text-light text-sm" data-id="element-1167">
                Please don't close this window.
              </p>
            </motion.div>}

          {step === 4 && <motion.div key="step4" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="py-6 flex flex-col items-center text-center space-y-6" data-id="element-1168">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center" data-id="element-1169">
                <CheckCircle2 size={40} className="text-green-500" data-id="element-1170" />
              </div>

              <div data-id="element-1171">
                <h3 className="text-2xl font-bold text-brand-text-dark mb-2" data-id="element-1172">
                  Thank you, {guestName.split(' ')[0]}!
                </h3>
                <p className="text-brand-text-light" data-id="element-1173">
                  Your donation of{' '}
                  <strong className="text-brand-text-dark" data-id="element-1174">
                    {formatCurrency(Number(amount))}
                  </strong>{' '}
                  was successful.
                </p>
              </div>

              <div className="w-full bg-gray-50 p-4 rounded-xl text-sm space-y-2 text-left" data-id="element-1175">
                <div className="flex justify-between" data-id="element-1176">
                  <span className="text-gray-500" data-id="element-1177">Reference</span>
                  <span className="font-mono font-medium" data-id="element-1178">
                    {transactionRef}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-1179">
                  <span className="text-gray-500" data-id="element-1180">Campaign</span>
                  <span className="font-medium truncate max-w-[180px]" data-id="element-1181">
                    {selectedCampaign?.title}
                  </span>
                </div>
              </div>

              <div className="w-full space-y-3 pt-4" data-id="element-1182">
                <Link to="/login" className="w-full flex items-center justify-center py-3 bg-brand-primary/10 text-brand-primary-dark font-semibold rounded-xl hover:bg-brand-primary/20 transition-colors" data-id="element-1183">
                  Create an account to track donations
                </Link>
                <button onClick={() => setIsModalOpen(false)} className="w-full py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors" data-id="element-1184">
                  Donate to Another Cause
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </div>;
}